// Which checklist items a work order shows: the items of its type's checklist — the active
// ones, plus inactive ones the work order already holds an answer for (an admin deactivating
// an item must not hide an old work order's answer). A type without checklist shows nothing,
// whatever the work order still carries from an earlier type (same rule as the hard-coded
// list it replaced). Dependency-free: the portal imports this file directly.

export interface IChecklistItemLike {
    work_order_checklist_item_id: number;
    work_order_checklist_id: number;
    code: string;
    name: string;
    sequence: number;
    active: boolean;
}

export interface IChecklistLike<Item extends IChecklistItemLike = IChecklistItemLike> {
    work_order_checklist_id: number;
    active: boolean;
    items: Item[];
}

export function resolveChecklistItems<Item extends IChecklistItemLike>(
    workOrderChecklistId: number | null | undefined,
    checklists: IChecklistLike<Item>[],
    storedAnswers: Record<string, boolean> | null | undefined,
): Item[] {
    const linked = checklists.find(checklist => checklist.work_order_checklist_id === workOrderChecklistId);
    if (!linked?.active) return [];
    const answered = new Set(Object.keys(storedAnswers ?? {}));
    return linked.items
        .filter(item => item.active || answered.has(item.code))
        .sort((a, b) => a.sequence - b.sequence || a.code.localeCompare(b.code));
}

// What gets saved: one key per shown item (false when unticked, so "key present" means it
// was offered), nothing else — answers for items outside the type's checklist and unknown
// keys from stale clients are dropped. Undefined when the type has no checklist.
export function normalizeChecklistAnswers<Item extends IChecklistItemLike>(
    shownItems: Item[],
    storedAnswers: Record<string, boolean> | null | undefined,
): Record<string, boolean> | undefined {
    if (!shownItems.length) return undefined;
    return Object.fromEntries(shownItems.map(item => [item.code, storedAnswers?.[item.code] === true]));
}

// Labels of the ticked items, in checklist order — what the PDF prints.
export function checkedItemNames<Item extends IChecklistItemLike>(
    items: Item[],
    storedAnswers: Record<string, boolean> | null | undefined,
): string[] {
    return [...items]
        .sort((a, b) => a.sequence - b.sequence)
        .filter(item => storedAnswers?.[item.code] === true)
        .map(item => item.name);
}
